/**
 * CORE-G-10 count-badge primitive.
 *
 * Chans's 2026-09-15 rebuild spec §7 G-10:
 *   "Badges on the Management category bar as well as on the left nav."
 *
 * Two visual variants ship in the codebase today:
 *   - Left nav's `NavBadge` (ModernMainLayout.tsx) — coral pill with
 *     numeric or "N pending" text depending on `badgeKey`.
 *   - Management category bar's per-tab count — small red pill, numeric.
 *
 * This is the primitive for the CATEGORY-BAR variant. The two badges
 * share Chans's core rule ("renders only when count > 0"), so a reader
 * can trace both back to this file's docstring rather than two hidden
 * copies of the guard.
 *
 * Why numeric-only, capped at 99+
 *
 *   The category bar has seven tabs; a "N pending" label on each would
 *   double the bar's width and push tabs off-screen. Numeric is what
 *   fits, and the cap prevents runaway rendering when a broken read
 *   returns a huge value.
 *
 * ⛔ Renders NULL when count is falsy or zero — a bar full of zero pills
 * is worse than no pills and hides the tabs that actually need action.
 */

interface CountBadgeProps {
  count: number | undefined;
  /**
   * Optional label prefix for screen readers. Default: "open items". The
   * ARIA label reads e.g. "3 open items"; operators using assistive tech
   * hear the count and its intent, not just the number.
   */
  label?: string;
  className?: string;
}

export function CountBadge({
  count,
  label = 'open item',
  className,
}: CountBadgeProps) {
  if (!count) return null;
  const suffix = count === 1 ? label : `${label}s`;
  return (
    <span
      aria-label={`${count} ${suffix}`}
      className={
        'ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center ' +
        'rounded-full bg-destructive px-1.5 text-[10px] font-semibold ' +
        'leading-4 text-destructive-foreground' +
        (className ? ` ${className}` : '')
      }
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export default CountBadge;
